import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, CheckSquare, Square, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { formatNumberDesktop, parseNumberDesktop, handleNumberInputDesktop } from '../../utils/calculations';
import { getColSpan, applyInputStyles, applyLabelStyles, compensarEscalaVh } from '../../utils/formHelpers';
import SqlexecMasterDetail from '../sqlexec/SqlexecMasterDetail';
import SqlexecConfirmDialog from '../sqlexec/SqlexecConfirmDialog';
import { secureFetch } from '../../utils/authInterceptor';

/**
 * GridMultiSelect - Componente reutilizable para selección múltiple con checkboxes
 * 
 * @component
 * @param {Object} config - Configuración completa desde JSON
 * @param {string} config.query - Query SQL para obtener datos disponibles
 * @param {string} config.idField - Nombre del campo ID para selección
 * @param {string} config.parentKey - Nombre del campo padre (ej: idinvearts)
 * @param {Array} config.columns - Definición de columnas
 * @param {number} parentValue - Valor del ID padre
 * @param {string} componentForm - Nombre del componente (para endpoint)
 * @param {Object} schema - Schema completo (para obtener title)
 * @param {Function} onClose - Callback al cerrar modal
 * @param {Function} onSuccess - Callback al guardar exitosamente
 */
const GridMultiSelect = ({ 
  config, 
  parentValue, 
  parentRow,
  componentForm,
  schema,
  onClose, 
  onSuccess 
}) => {
  const { tabla_ui, modales_ui, botones_ui, formulario_ui } = useSisConfig();

  // ==========================================
  // CONFIGURACIÓN UI
  // ==========================================
  const headerConfig = tabla_ui?.header || {};
  const rowsConfig = tabla_ui?.rows || {};
  const footerConfig = modales_ui?.footer || {};
  const btnBase = botones_ui || {};
  //const btnPrimary = botones_ui?.primary || {};
  const btnSecondary = botones_ui?.secondary || {};
  const btnSuccess   = botones_ui?.success   || {};
  const overlayConfig = modales_ui?.overlay || {};
  const containerConfig = modales_ui?.container || {};
  const modalHeaderConfig = modales_ui?.header || {};
  const closeButtonConfig = modales_ui?.closeButton || {};
  const checkboxConfig = tabla_ui?.booleanDisplay?.checkbox || {};
  const inputConfig = formulario_ui?.inputs || {};
  const labelConfig = formulario_ui?.labels || {};
  const gridConfig = formulario_ui?.grid || { columns: 6 };
  const [pendingGuardar, setPendingGuardar] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // ==========================================
  // ESTADO
  // ==========================================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [complementoOptions, setComplementoOptions] = useState([]);
  const [opcionesColumna, setOpcionesColumna] = useState({});
  const opcionesEnCarga = useRef(new Set());
  const montado = useRef(true);
  // StrictMode (desarrollo) monta, desmonta y vuelve a montar: el efecto debe
  // reponer montado=true en cada montaje, no solo apagarlo al desmontar.
  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  // ==========================================
  // COLUMNAS EDITABLES CONFIGURABLES (JSONB)
  // ==========================================
  // Una columna de config.columns con "editable" se resuelve de forma
  // generica: control (editable.type), opciones (dataSource, con params
  // tomados de la misma fila), obligatoriedad (editable.requiredWhen) y su
  // lugar en el payload. Las columnas sin "editable" siguen por las ramas
  // existentes de abajo, sin cambios.
  const columnasEditables = (config?.columns || []).filter(c => c?.editable?.type === 'select');

  const valorFila = (row, id, campo) => editedValues[`${campo}_${id}`] ?? row?.[campo] ?? '';

  const claveOpciones = (col, row, id) =>
    `${col.campo}|${(col.dataSource?.params || []).map(p => valorFila(row, id, p)).join('|')}`;

  // ==========================================
  // CARGAR DATOS
  // ==========================================
  useEffect(() => {
    let isMounted = true; // 🔥 Flag para evitar double-call
    
    const loadData = async () => {
      if (!config?.query || !parentValue) {
        toast.error('Configuración incompleta para GridMultiSelect');
        return;
      }

      setLoading(true);
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        
        const query = config.query;
        const params = [parentValue];
        
        const response = await secureFetch('/api/sqlexec_generico/query-params', {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ 
            query: query.replace(':parentValue', '$1'),
            params: params
          })
        });

        const result = await response.json();

        if (result?.success) {
          const datos = result.data || [];
          setData(datos);
          
          // 🔥 Solo ejecutar si el componente sigue montado
          if (isMounted && datos.length === 0) {
            const titulo = schema?.title || 'elementos';
            
            toast.info(
              `No hay ${titulo.toLowerCase()} disponibles para agregar`,
              { autoClose: 2000 }
            );
            
            setTimeout(() => {
              onClose?.();
            }, 500);
          }
          
        } else {
          toast.error('Error cargando datos disponibles');
        }
      } catch (error) {
        //console.error('Error en GridMultiSelect:', error);
        toast.error('Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Cleanup function
    return () => {
      isMounted = false; // 🔥 Marca como desmontado
    };
  }, [config, parentValue]);

  // 🔥 Cargar catálogo de complementos (satcomplementos) si la columna lo declara --
  // mismo endpoint/dataSource que usa SqlexecBuilder para selects con query, sin params.
  useEffect(() => {
    const col = config?.columns?.find(c => c.campo === 'idcomplemento');
    if (!col?.dataSource?.query || col.editable) return;

    let isMounted = true;
    const loadComplementos = async () => {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const response = await secureFetch('/api/sqlexec/query', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query: col.dataSource.query })
        });
        const result = await response.json();
        if (isMounted && result?.success) {
          setComplementoOptions(result.data || []);
        }
      } catch (error) {
        toast.error('Error cargando catálogo de complementos');
      }
    };

    loadComplementos();
    return () => { isMounted = false; };
  }, [config]);

  // Opciones de columnas editables: sin params se cargan una vez; con params
  // se cargan por cada combinacion de valores de las filas seleccionadas.
  useEffect(() => {
    columnasEditables.forEach(col => {
      if (!col.dataSource?.query) return;
      const params = col.dataSource.params || [];
      const filas = params.length === 0
        ? [null]
        : Array.from(selectedIds).map(id => data.find(r => r[config.idField] === id)).filter(Boolean);

      filas.forEach(row => {
        const id = row ? row[config.idField] : null;
        const valores = params.map(p => valorFila(row, id, p));
        if (valores.some(v => v === '' || v === null || v === undefined)) return;
        const clave = `${col.campo}|${valores.join('|')}`;
        if (clave in opcionesColumna || opcionesEnCarga.current.has(clave)) return;
        opcionesEnCarga.current.add(clave);

        (async () => {
          try {
            const token = sessionStorage.getItem('mapasac_access_token');
            const conParams = valores.length > 0;
            const response = await secureFetch(conParams ? '/api/sqlexec_generico/query-params' : '/api/sqlexec/query', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(conParams
                ? { query: col.dataSource.query, params: valores }
                : { query: col.dataSource.query })
            });
            const result = await response.json();
            if (montado.current && result?.success) {
              setOpcionesColumna(prev => ({ ...prev, [clave]: result.data || [] }));
            } else if (montado.current) {
              toast.error(`Error cargando opciones de ${col.alias || col.campo}`);
            }
          } catch (error) {
            if (montado.current) toast.error(`Error cargando opciones de ${col.alias || col.campo}`);
          } finally {
            opcionesEnCarga.current.delete(clave);
          }
        })();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, data, selectedIds, editedValues, opcionesColumna]);

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleToggle = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
        setEditedValues(prev => {
        const next = { ...prev };
        delete next[`nodo_xml_${id}`];
        delete next[`aplica_regimen_${id}`];
        delete next[`orden_calculo_${id}`];
        delete next[`base_calculo_${id}`];
        delete next[`idcomplemento_${id}`];
        columnasEditables.forEach(c => { delete next[`${c.campo}_${id}`]; });
        return next;
      });
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === data.length && data.length > 0);
  };

  const handleToggleAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(data.map(row => row[config.idField]));
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  };

  const handleEditableChange = (id, campo, valor) => {
    setEditedValues(prev => {
      const next = { ...prev, [`${campo}_${id}`]: valor };
      columnasEditables
        .filter(c => (c.dataSource?.params || []).includes(campo))
        .forEach(c => { delete next[`${c.campo}_${id}`]; });
      return next;
    });
  };

  const handleValueChange = (id, newValue) => {
    setEditedValues(prev => ({
      ...prev,
      [id]: newValue
    }));
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Selecciona al menos un elemento');
      return;
    }

    // Obligatoriedad por configuracion (editable.requiredWhen.field): la
    // columna es requerida en las filas donde ese campo tiene valor.
    for (const col of columnasEditables) {
      const regla = col.editable.requiredWhen;
      if (!regla?.field) continue;
      const faltantes = Array.from(selectedIds)
        .map(id => data.find(r => r[config.idField] === id))
        .filter(row => {
          const id = row[config.idField];
          const condicion = valorFila(row, id, regla.field);
          const valor = valorFila(row, id, col.campo);
          return condicion !== '' && condicion !== null && (valor === '' || valor === null);
        });
      if (faltantes.length > 0) {
        const filas = faltantes.map(r => r[regla.rowLabel] ?? r[config.idField]).join(', ');
        toast.error(`${col.editable.requiredMessage || `${col.alias} es requerido`}: ${filas}`, { autoClose: 5000 });
        return;
      }
    }

    if (!pendingGuardar) {
      setPendingGuardar(true);
      return;
    }

    // 🔥 VALIDACIÓN: Si rango="RANGO" → valor debe ser > 0
    const invalidos = Array.from(selectedIds)
      .map(id => data.find(r => r[config.idField] === id))
      .filter(row => {
        const id = row[config.idField];
        const valorFinal = editedValues[id] ?? row?.valor; // 🔥 Usar valor editado
        const esRango = row?.rango === 'RANGO';
        const valorInvalido = !valorFinal || parseFloat(valorFinal) <= 0;
        return esRango && valorInvalido;
      });

    if (invalidos.length > 0) {
      const codigos = invalidos.map(r => r.c_impuesto || r.codigo).join(', ');
      toast.error(
        `${invalidos.length} tasa(s) con RANGO requieren valor > 0: ${codigos}`,
        { autoClose: 5000 }
      );
      return; // ← BLOQUEA guardado
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      
      const promises = Array.from(selectedIds).map(id => {
        const row = data.find(r => r[config.idField] === id);
        
        const payload = {
          idinvearts:     parentValue,
          idsattasocuo:   id,
          valor:          editedValues[id] ?? row?.valor ?? 0,
          activo:         true,
          nodo_xml:       editedValues[`nodo_xml_${id}`] ?? (row?.retencion ? 'Retenciones' : 'Traslados'),
          orden_calculo:  editedValues[`orden_calculo_${id}`] ?? (Array.from(selectedIds).indexOf(id) + 1),
          base_calculo:   editedValues[`base_calculo_${id}`] ?? 'subtotal',
          aplica_regimen: JSON.stringify(JSON.parse(editedValues[`aplica_regimen_${id}`] ?? '[]')),
          idcomplemento:  editedValues[`idcomplemento_${id}`] || null,
        };

        // Columnas configurables: siempre presentes en el payload (el backend
        // arma los $N por posicion segun schema_form.fields).
        columnasEditables.forEach(col => {
          const v = valorFila(row, id, col.campo);
          payload[col.campo] = (v === '' || v === undefined) ? null : v;
        });

        //console.log('🔍 aplica_regimen raw:', editedValues[`aplica_regimen_raw_${id}`]);
        //console.log('🔍 aplica_regimen final:', editedValues[`aplica_regimen_${id}`]);
        //console.log('🔍 payload:', JSON.stringify(payload));

        return secureFetch(`/api/sqlexec/${componentForm}/create`, {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        });
      });

      const responses = await Promise.all(promises);
      const results = await Promise.all(responses.map(r => r.json()));
      const allSuccess = results.every(r => r.success);
      const failedCount = results.filter(r => !r.success).length;

      if (allSuccess) {
        toast.success(`${selectedIds.size} elemento(s) agregado(s) correctamente`);
        onSuccess?.();
        onClose?.();
      } else if (failedCount === results.length) {
        const detalle = results.find(r => !r.success)?.error?.message;
        toast.error(detalle || 'Error: No se pudo agregar ningún elemento', { autoClose: detalle ? 8000 : 5000 });
      } else {
        const successCount = results.length - failedCount;
        const detalle = results.find(r => !r.success)?.error?.message;
        toast.warning(`${successCount} agregado(s), ${failedCount} fallaron${detalle ? ` -- ${detalle}` : ''}`, { autoClose: 8000 });
        onSuccess?.();
        onClose?.();
      }
    } catch (error) {
      //console.error('Error guardando selección:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // RENDER HELPERS
  // ==========================================

  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';

    switch (column.tipo) {
      case 'boolean':
        // 🔥 Checkbox visual con iconos desde config
        return value 
          ? <Check size={checkboxConfig.size || 20} style={{color: checkboxConfig.trueColor || '#3b82f6'}} />
          : <X size={checkboxConfig.size || 20} style={{color: checkboxConfig.falseColor || '#9ca3af'}} />;
      
      case 'percentage':
        const percent = parseFloat(value) * 100;
        return `${percent.toFixed(column.decimals || 2)}%`;
      
      case 'number':
        return parseFloat(value).toFixed(column.decimals || 2);
      
      default:
        return value;
    }
  };

  // 🔥 FUNCIÓN: Obtener alineación según tipo de columna (IGUAL QUE SqlexecTable)
  // Ahora con detección automática de booleanos y lectura 100% desde JSON
  const getColumnAlign = (column, valor = null, formattedValue = null) => {
    const tipo = column.tipo;
    const format = column.format;
    
    // ========================================
    // PRIORIDAD 1: Formato específico
    // ========================================
    if (format === 'money') {
      return tabla_ui?.columnAlignment?.money || 'left';
    }
    if (format === 'percentage') {
      return tabla_ui?.columnAlignment?.percentage || 'center';
    }
    
    // ========================================
    // PRIORIDAD 2: Detectar booleanos
    // (schema O valor O JSX renderizado)
    // ========================================
    const esBoolean = 
      tipo === 'boolean' ||
      typeof valor === 'boolean' ||
      (React.isValidElement(formattedValue) && 
       (formattedValue.type === Check || formattedValue.type === X));
    
    if (esBoolean) {
      return tabla_ui?.columnAlignment?.boolean || 'center'; // ← Lee desde JSON
    }
    
    // ========================================
    // PRIORIDAD 3: Tipo de dato genérico
    // ========================================
    return tabla_ui?.columnAlignment?.[tipo] || 
           tabla_ui?.columnAlignment?.text || 
           'left';
  };

  // ==========================================
  // RENDER
  // ==========================================

  if (loading) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{
          backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.1)',  //fondo de pantalla
          backdropFilter: modales_ui?.overlay?.backdropBlur ? `blur(${modales_ui?.overlay?.backdropBlur})` : 'blur(4px)'       }}
      >
        <div 
          className="rounded-lg p-8"
          style={{
            backgroundColor: containerConfig.backgroundColor || '#ffffff',
            borderRadius: containerConfig.borderRadius || '12px'
          }}
        >
          <Loader2 className="animate-spin text-blue-500" size={48} />
        </div>
      </div>
    );
  }

  const visibleColumns = config.columns?.filter(col => col.visible !== false) || [];
  const modalTitle = schema?.title || 'Seleccionar Elementos';

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-2"
      style={{
        backgroundColor: overlayConfig.backgroundColor || 'rgba(0, 0, 0, 0.6)',
        backdropFilter: overlayConfig.backdropBlur ? `blur(${overlayConfig.backdropBlur})` : 'blur(4px)'
      }}
    >
      {/* Container del modal */}
      <div
        className="w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: containerConfig.backgroundColor || '#ffffff',
          borderRadius: containerConfig.borderRadius || '12px',
          boxShadow: containerConfig.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: containerConfig.border || '1px solid rgba(0, 0, 0, 0.05)',
          maxWidth: containerConfig.maxWidth === '4xl' ? '64rem' : compensarEscalaVh(containerConfig.maxWidth) || '64rem',
          maxHeight: compensarEscalaVh(containerConfig.maxHeight || '95vh'),
          minHeight: compensarEscalaVh(containerConfig.minHeight || '85vh'),
          overflow: containerConfig.overflow || 'hidden'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between"
          style={{
            paddingLeft: modalHeaderConfig.paddingX || '2rem',
            paddingRight: modalHeaderConfig.paddingX || '2rem',
            paddingTop: modalHeaderConfig.paddingY || '1.5rem',
            paddingBottom: modalHeaderConfig.paddingY || '1.5rem',
            borderBottom: modalHeaderConfig.borderBottom || '1px solid #e5e7eb',
            backgroundColor: modalHeaderConfig.backgroundColor || '#fafafa'
          }}
        >
          <div className="flex items-center" style={{ gap: modalHeaderConfig.iconGap || '1rem' }}>
            <div 
              className="bg-blue-100"
              style={{
                padding: modalHeaderConfig.iconPadding || '0.625rem',
                borderRadius: modalHeaderConfig.iconBorderRadius || '10px'
              }}
            >
              <CheckSquare 
                className="text-blue-600" 
                size={parseInt(modalHeaderConfig.iconSize) || 28} 
              />
            </div>
            <h2 
              className="font-semibold"
              style={{
                fontSize: modalHeaderConfig.fontSize || '1.5rem',
                fontWeight: modalHeaderConfig.fontWeight || '600',
                color: modalHeaderConfig.textColor || '#111827',
                lineHeight: modalHeaderConfig.lineHeight || '1.2',
                letterSpacing: modalHeaderConfig.letterSpacing || '-0.025em'
              }}
            >
              {modalTitle}
            </h2>
          </div>

          <button 
            onClick={onClose} 
            className="transition-colors"
            style={{
              padding: closeButtonConfig.padding || '0.5rem',
              borderRadius: closeButtonConfig.borderRadius || '8px',
              color: closeButtonConfig.color || '#6b7280',
              transition: closeButtonConfig.transition || 'all 150ms ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = closeButtonConfig.hoverBackgroundColor || '#f3f4f6';
              e.target.style.color = closeButtonConfig.hoverColor || '#111827';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = closeButtonConfig.color || '#6b7280';
            }}
          >
            <X size={parseInt(closeButtonConfig.size) || 20} />
          </button>
        </div>

        {/* Contenido */}
        <div 
          className="flex-1 overflow-auto"
          style={{
            paddingLeft: modales_ui?.content?.paddingX || '0.75rem',
            paddingRight: modales_ui?.content?.paddingX || '0.75rem',
            paddingTop: modales_ui?.content?.paddingTop || '0.5rem',
            paddingBottom: modales_ui?.content?.paddingBottom || '0.5rem',
            minHeight: compensarEscalaVh(modales_ui?.content?.minHeight || '70vh'),  // 🆕 Desde config
            flex: modales_ui?.content?.flex || '1 1 auto'         // 🆕 Desde config
          }}
        >
          {/* 🔥 NUEVO: Campos de contexto desde parentRow */}
          {config.contextFields && parentRow && (
            <div style={{ 
              marginBottom: gridConfig.gap || '1rem', // ✅ Desde config (no hardcode)
              display: 'grid',
              gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`,
              gap: gridConfig.gap || '1rem'
            }}>
              {config.contextFields.map((field, idx) => (
                <div 
                  key={idx}
                  style={{
                    gridColumn: `span ${getColSpan(field.gridSpan, gridConfig)}`
                  }}
                >
                  <label style={applyLabelStyles(labelConfig)}>
                    {field.label}
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={parentRow[field.name] || ''}
                    className="w-full" // ✅ Como SqlexecBuilder
                    style={applyInputStyles(inputConfig, true, false)}
                  />
                </div>
              ))}
            </div>
          )}
          {data.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No hay elementos disponibles para seleccionar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table 
                className="w-full"
                style={{
                  border: '1px solid #d1d5db',
                  borderCollapse: 'collapse',
                  tableLayout: 'auto',
                  widht: '100%'
                }}
              >
                <thead
                  style={{
                    position: headerConfig.sticky ? 'sticky' : 'static',
                    top: headerConfig.sticky ? (headerConfig.stickyTop || 0) : 'auto',
                    zIndex: headerConfig.sticky ? 10 : 'auto'
                  }}
                >
                  <tr style={{ 
                    backgroundColor: headerConfig.backgroundColor || '#f9fafb',
                    borderBottom: headerConfig.borderBottom 
                      ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                      : '2px solid #e5e7eb'
                  }}>
                    {/* Columna checkbox "Seleccionar todos" */}
                    <th 
                      className="text-center"
                      style={{
                        paddingLeft: headerConfig.padding?.x || '0.50rem',
                        paddingRight: headerConfig.padding?.x || '0.50rem',
                        paddingTop: headerConfig.padding?.y || '0.50rem',
                        paddingBottom: headerConfig.padding?.y || '0.50rem',
                        fontSize: headerConfig.fontSize || '0.75rem',
                        fontFamily: headerConfig.fontFamily || 'inherit',
                        fontWeight: headerConfig.fontWeight || '600',
                        color: headerConfig.color || '#374151',
                        width: '50px',
                        textAlign: 'left',
                        borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                        borderTop: headerConfig.borderTop || '1px solid #d1d5db',
                      }}
                    >
                      <button
                        onClick={handleToggleAll}
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }} // 🔥 Solo inline styles
                      >
                        {selectAll ? (
                          <CheckSquare size={20} className="text-white" />
                        ) : (
                          <Square size={20} className="text-white" />
                        )}
                      </button>
                    </th>

                    {/* Columnas configuradas - 🔥 ALINEACIÓN 100% DESDE JSON */}
                    {visibleColumns.map((col, idx) => (
                      <th 
                        key={idx}
                        style={{
                          paddingLeft: headerConfig.padding?.x || '0.50rem',
                          paddingRight: headerConfig.padding?.x || '0.50rem',
                          paddingTop: headerConfig.padding?.y || '0.50rem',
                          paddingBottom: headerConfig.padding?.y || '0.50rem',
                          fontSize: headerConfig.fontSize || '0.75rem',
                          fontFamily: headerConfig.fontFamily || 'inherit',
                          fontWeight: headerConfig.fontWeight || '600',
                          color: headerConfig.color || '#374151',
                          textAlign: col.align || (col.tipo === 'boolean' ? 'left' : getColumnAlign(col)), // 🔥 Lee desde JSON: boolean → "center"
                          textTransform: headerConfig.textTransform || 'uppercase',
                          width: col.width || 'auto',
                          borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                          borderTop: headerConfig.borderTop || '1px solid #d1d5db'
                        }}
                      >
                        {col.alias}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {data.map((row, rowIdx) => {
                    const id = row[config.idField];
                    const isSelected = selectedIds.has(id);

                    // 🔥 SINCRONIZADO: Usar rowsConfig para background
                    const backgroundColor = rowIdx % 2 === 0 
                      ? (rowsConfig.odd || '#ffffff')
                      : (rowsConfig.even || '#f9fafb');

                    return (
                      <tr 
                        key={rowIdx}
                        className="transition-colors cursor-pointer"
                        onClick={() => handleToggle(id)}
                        style={{
                          backgroundColor: isSelected 
                            ? (tabla_ui?.rowSelection?.backgroundColor || '#eff6ff')
                            : backgroundColor,
                          borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb',
                          height: rowsConfig.height || '32px' // 🔥 SINCRONIZADO: Altura desde config
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = rowsConfig.hoverColor || '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = backgroundColor;
                          }
                        }}
                      >
                        {/* Checkbox - 🔥 PADDING AJUSTADO */}
                        <td 
                          className="text-center"
                          style={{
                            paddingLeft: '0.5rem',
                            paddingRight: '0.5rem',
                            paddingTop: '0.125rem', //'0.5rem',    // 🔥 Aumentado para altura visual correcta
                            paddingBottom: '0.125rem', //'0.5rem', // 🔥 Aumentado para altura visual correcta
                            fontSize: rowsConfig.fontSize || '0.75rem',
                            fontFamily: rowsConfig.fontFamily || 'inherit',
                            color: rowsConfig.textColor || '#111827',
                            borderRight: rowsConfig.borderRight || '1px solid #e5e7eb',
                            //verticalAlign: 'middle'
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: '100%'
                          }}>
                            {isSelected ? (
                              <CheckSquare size={20} className="text-blue-600" />
                            ) : (
                              <Square size={20} className="text-gray-400" />
                            )}
                          </div>
                        </td>

                        {/* Datos - 🔥 ALINEACIÓN 100% DESDE JSON */}
                        {visibleColumns.map((col, colIdx) => {
                          const valor = row[col.campo];
                          const formattedValue = formatValue(valor, col);
                          const id = row[config.idField]; // ✅ Declarar AQUÍ (scope del map)
                          
                          // Columna editable configurable (JSONB): select con opciones del
                          // dataSource; si depende de otro campo de la fila y este no tiene
                          // valor, queda deshabilitada.
                          if (col.editable?.type === 'select') {
                            const vf = col.dataSource?.valueField || 'value';
                            const lf = col.dataSource?.labelField || 'label';
                            const faltaParam = (col.dataSource?.params || []).some(p => valorFila(row, id, p) === '');
                            const opciones = faltaParam ? [] : (opcionesColumna[claveOpciones(col, row, id)] || []);
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected ? (
                                  <select
                                    value={valorFila(row, id, col.campo)}
                                    disabled={faltaParam}
                                    onChange={(e) => handleEditableChange(id, col.campo, e.target.value)}
                                    style={{
                                      ...applyInputStyles(inputConfig, faltaParam, false),
                                      width: '100%',
                                      fontSize: rowsConfig.fontSize || '0.75rem'
                                    }}
                                  >
                                    <option value="">{col.editable.emptyLabel ?? ''}</option>
                                    {opciones.map(opt => (
                                      <option key={opt[vf]} value={opt[vf]}>{opt[lf]}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ color: inputConfig.placeholderColor || '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          // 🔥 Si es columna "valor" → input editable con formato desktop
                          if (col.campo === 'valor') {
                            const esRango = row.rango === 'RANGO';
                            const currentValue = editedValues[id] ?? valor; // ✅ Usar id del scope superior

                            return (
                              <td
                                key={colIdx}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.5rem',
                                  paddingBottom: '0.5rem',
                                  fontSize: rowsConfig.fontSize || '0.75rem',
                                  fontFamily: rowsConfig.fontFamily || 'inherit',
                                  color: rowsConfig.textColor || '#111827',
                                  textAlign: getColumnAlign(col) || 'right',
                                  width: '120px',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected && esRango ? (
                                  <input
                                    type="text"
                                    onClick={(e) => e.stopPropagation()}
                                    value={focusedField === `valor-${id}` ? (currentValue ?? '') : formatNumberDesktop(currentValue, col.decimals || 6)}
                                    onChange={(e) => {
                                      let valor = e.target.value;
                                      // Solo números y un punto decimal
                                      valor = valor.replace(/[^0-9.]/g, '');
                                      const parts = valor.split('.');
                                      if (parts.length > 2) valor = parts[0] + '.' + parts.slice(1).join('');
                                      // Máximo N decimales (según sqlexec_columnas.decimals)
                                      if (parts[1] && parts[1].length > (col.decimals || 6)) {
                                        valor = parts[0] + '.' + parts[1].substring(0, col.decimals || 6);
                                      }
                                      handleValueChange(id, valor);
                                    }}
                                    onFocus={(e) => {
                                      setFocusedField(`valor-${id}`);
                                      requestAnimationFrame(() => e.target.select());
                                    }}
                                    onBlur={(e) => {
                                      setFocusedField(null);
                                      const num = parseFloat(e.target.value);
                                      if (!isNaN(num) && num > 0) {
                                        handleValueChange(id, parseFloat(num.toFixed(col.decimals || 6)));
                                      } else {
                                        handleValueChange(id, 0);
                                      }
                                    }}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '100%',
                                      maxWidth: '110px',
                                      textAlign: getColumnAlign(col) || 'right',
                                      outline: 'none',
                                      borderColor: focusedField === `valor-${id}` ? (inputConfig.focusBorderColor || '#3b82f6') : (inputConfig.borderColor || '#d1d5db'),
                                      boxShadow: focusedField === `valor-${id}` ? `0 0 0 ${inputConfig.focusRingWidth || '2px'} ${inputConfig.focusBorderColor || '#3b82f6'}40` : 'none',
                                    }}
                                  />
                                ) : (
                                  formatNumberDesktop(formattedValue, col.decimals || 6)
                                )}
                              </td>
                            );
                          }

                          if (col.campo === 'nodo_xml') {
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb',
                                }}
                              >
                                {isSelected ? (
                                  <select
                                    value={editedValues[`nodo_xml_${id}`] ?? (row?.retencion ? 'Retenciones' : 'Traslados')}
                                    onChange={(e) => setEditedValues(prev => ({
                                      ...prev,
                                      [`nodo_xml_${id}`]: e.target.value
                                    }))}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '100%',
                                      fontSize: rowsConfig.fontSize || '0.75rem'
                                    }}
                                  >
                                    <option value="Traslados">Traslados</option>
                                    <option value="Retenciones">Retenciones</option>
                                    <option value="TrasladosLocales">Traslados Locales</option>
                                    <option value="RetencionesLocales">Retenciones Locales</option>
                                  </select>
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          if (col.campo === 'orden_calculo') {
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected ? (
                                  <input
                                    type="number"
                                    min={1}
                                    max={99}
                                    value={editedValues[`orden_calculo_${id}`] ?? (Array.from(selectedIds).indexOf(id) + 1)}
                                    onChange={(e) => setEditedValues(prev => ({
                                      ...prev,
                                      [`orden_calculo_${id}`]: parseInt(e.target.value) || 1
                                    }))}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '60px',
                                      fontSize: rowsConfig.fontSize || '0.75rem',
                                      textAlign: 'center'
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          if (col.campo === 'base_calculo') {
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected ? (
                                  <select
                                    value={editedValues[`base_calculo_${id}`] ?? 'subtotal'}
                                    onChange={(e) => setEditedValues(prev => ({
                                      ...prev,
                                      [`base_calculo_${id}`]: e.target.value
                                    }))}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '100%',
                                      fontSize: rowsConfig.fontSize || '0.75rem'
                                    }}
                                  >
                                    <option value="subtotal">Subtotal</option>
                                    <option value="subtotal_mas_traslados">Subtotal + Traslados</option>
                                  </select>
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          if (col.campo === 'aplica_regimen') {
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected ? (
                                  <input
                                    type="text"
                                    placeholder="601,603,612 o vacío=todos"
                                    value={editedValues[`aplica_regimen_raw_${id}`] ?? ''}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/[^0-9]/g, '');
                                      let formatted = '';
                                      const valores = [];
                                      for (let i = 0; i < raw.length; i += 3) {
                                        const chunk = raw.substring(i, i + 3);
                                        if (chunk.length === 3) {
                                          formatted += (formatted ? ',' : '') + chunk;
                                          valores.push(parseInt(chunk));
                                        }
                                      }
                                      setEditedValues(prev => ({
                                        ...prev,
                                        [`aplica_regimen_raw_${id}`]: raw,
                                        [`aplica_regimen_${id}`]: JSON.stringify(valores)
                                      }));
                                    }}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '100%',
                                      fontSize: rowsConfig.fontSize || '0.75rem'
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          if (col.campo === 'idcomplemento') {
                            return (
                              <td
                                key={colIdx}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  paddingLeft: '0.5rem',
                                  paddingRight: '0.5rem',
                                  paddingTop: '0.25rem',
                                  paddingBottom: '0.25rem',
                                  borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                                }}
                              >
                                {isSelected ? (
                                  <select
                                    value={editedValues[`idcomplemento_${id}`] ?? ''}
                                    onChange={(e) => setEditedValues(prev => ({
                                      ...prev,
                                      [`idcomplemento_${id}`]: e.target.value
                                    }))}
                                    style={{
                                      ...applyInputStyles(inputConfig, false, false),
                                      width: '100%',
                                      fontSize: rowsConfig.fontSize || '0.75rem'
                                    }}
                                  >
                                    <option value="">SIN COMPLEMENTO</option>
                                    {complementoOptions.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: rowsConfig.fontSize || '0.75rem' }}>—</span>
                                )}
                              </td>
                            );
                          }

                          // Resto de columnas (normal)
                          const align = getColumnAlign(col, valor, formattedValue);
                          
                          return (
                            <td 
                              key={colIdx}
                              style={{
                                paddingLeft: '0.5rem',
                                paddingRight: '0.5rem',
                                paddingTop: '0.5rem',
                                paddingBottom: '0.5rem',
                                fontSize: rowsConfig.fontSize || '0.75rem',
                                fontFamily: rowsConfig.fontFamily || 'inherit',
                                color: rowsConfig.textColor || '#111827',
                                textAlign: align,
                                borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                              }}
                            >
                              {formattedValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="flex justify-between items-center"
          style={{
            paddingLeft: footerConfig.paddingX || '2rem',
            paddingRight: footerConfig.paddingX || '2rem',
            paddingTop: footerConfig.paddingY || '1.25rem',
            paddingBottom: footerConfig.paddingBottom || footerConfig.paddingY || '1.25rem',
            borderTop: footerConfig.borderTop || '1px solid #e5e7eb',
            backgroundColor: footerConfig.backgroundColor || '#fafafa',
            gap: footerConfig.gap || '0.75rem'
          }}
        >
          {/* Contador */}
          <div 
            className="text-sm"
            style={{
              fontSize: '0.875rem',
              color: '#4b5563'
            }}
          >
            <span className="font-semibold">{selectedIds.size}</span> de{' '}
            <span className="font-semibold">{data.length}</span> seleccionado(s)
          </div>

          {/* Botones */}
          <div className="flex" style={{ gap: footerConfig.gap || '0.75rem' }}>
            <button 
              onClick={onClose}
              disabled={saving}
              className="transition-colors"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: saving 
                  ? (btnSecondary.disabledBorderColor || '#e5e7eb')
                  : (btnSecondary.borderColor || '#d1d5db'),
                backgroundColor: saving
                  ? (btnSecondary.disabledBackgroundColor || '#f3f4f6')
                  : (btnSecondary.backgroundColor || '#ffffff'),
                color: saving
                  ? (btnSecondary.disabledTextColor || '#9ca3af')
                  : (btnSecondary.textColor || '#374151'),
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? '0.5' : '1',
                transitionDuration: btnBase.transitionDuration || '150ms'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.target.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                  e.target.style.color = btnSecondary.hoverTextColor || '#111827';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.target.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                  e.target.style.color = btnSecondary.textColor || '#374151';
                }
              }}
            >
              Cancelar
            </button>

            <button 
              onClick={handleSave}
              disabled={saving || selectedIds.size === 0}
              className="transition-colors flex items-center"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: (saving || selectedIds.size === 0) 
                  ? (btnSuccess.disabledBorderColor || '#9ca3af') 
                  : (btnSuccess.borderColor || '#16a34a'),
                backgroundColor: (saving || selectedIds.size === 0) 
                  ? (btnSuccess.disabledBackgroundColor || '#9ca3af') 
                  : (btnSuccess.backgroundColor || '#16a34a'),
                color: (saving || selectedIds.size === 0)
                  ? (btnSuccess.disabledTextColor || '#ffffff')
                  : (btnSuccess.textColor || '#ffffff'),
                cursor: (saving || selectedIds.size === 0) ? 'not-allowed' : 'pointer',
                gap: '0.5rem',
                transitionDuration: btnBase.transitionDuration || '150ms'
              }}
              onMouseEnter={(e) => {
                if (!saving && selectedIds.size > 0) {
                  e.currentTarget.style.backgroundColor = btnSuccess.hoverBackgroundColor || '#15803d';
                  e.currentTarget.style.borderColor = btnSuccess.hoverBorderColor || '#15803d';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving && selectedIds.size > 0) {
                  e.currentTarget.style.backgroundColor = btnSuccess.backgroundColor || '#16a34a';
                  e.currentTarget.style.borderColor = btnSuccess.borderColor || '#16a34a';
                }
              }}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <span>Guardar</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {pendingGuardar && (
        schema?.confirm_simple === true ? (
          <SqlexecConfirmDialog
            key="confirm-guardar-simple"
            isOpen={true}
            tipo="success"
            pregunta={`¿Deseas agregar ${schema?.title || 'estos elementos'} seleccionadas?`}
            onClose={() => setPendingGuardar(false)}
            onConfirm={() => {
              setPendingGuardar(false);
              handleSave();
            }}
          />
        ) : (
          <SqlexecMasterDetail
            key="confirm-guardar"
            componentForm="Sys_Confirma_Mensaje"
            mode="create"
            useGenerico={true}
            initialData={{
              idconfirma: 1,
              mode:   'create_catalogo',
              titulo: schema?.title || '',
            }}
            onClose={() => setPendingGuardar(false)}
            onSuccess={() => {
              setPendingGuardar(false);
              handleSave();
            }}
          />
        )
      )}
    </div>
  );
};

export default GridMultiSelect;